import React, { useContext, useEffect, useState } from 'react';
import PageContainer from '../components/PageContainer';
import { API_BASE_URL } from '../config/api';
import {
  Zap,
  Plus,
  ChevronDown,
  ChevronRight,
  Trash2,
  Edit2,
  Link,
  Link2,
  Lock,
  Unlock,
  Search,
} from 'react-feather';
import { UserContext } from '../UserContext';
import '../styles/account.css';
import '../styles/admin.css';

const infoBoxStyle = {
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  border: '1px solid #d9d9d9',
  background: '#f8f9fc',
  color: '#6f7276',
  fontSize: '0.9rem',
  lineHeight: 1.8,
};

const errorBoxStyle = {
  color: '#8f1f16',
  padding: '0.75rem 1rem',
  background: 'rgba(231, 74, 59, 0.08)',
  borderRadius: '8px',
  border: '1px solid rgba(231, 74, 59, 0.22)',
  fontSize: '0.9rem',
};

const successBoxStyle = {
  color: '#047857',
  padding: '0.75rem 1rem',
  background: 'rgba(16, 185, 129, 0.08)',
  borderRadius: '8px',
  border: '1px solid rgba(16, 185, 129, 0.22)',
  fontSize: '0.9rem',
};

const warnBoxStyle = {
  color: '#92400e',
  padding: '0.75rem 1rem',
  background: 'rgba(245, 158, 11, 0.08)',
  borderRadius: '8px',
  border: '1px solid rgba(245, 158, 11, 0.22)',
  fontSize: '0.9rem',
};

const styledSelect = {
  flex: 1,
  padding: '0.55rem 0.75rem',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  background: '#ffffff',
  color: '#373737',
  fontSize: '0.9rem',
  height: '2.5rem',
  cursor: 'pointer',
};

const alignedRow = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'stretch',
};

const EMPTY_FORM = {
  name: '',
  description: '',
  capability_type: 'financial',
  is_access_public: false,
  is_modification_public: false,
  price: '',
  reoccurring: true,
  apply_endpoint: '',
  unapply_endpoint: '',
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

// ── helpers ──────────────────────────────────────────────────────────────────

function buildConfig(form) {
  const base = { price: parseFloat(form.price), reoccurring: form.reoccurring };
  if (form.capability_type === 'flow') {
    return { ...base, apply_endpoint: form.apply_endpoint, unapply_endpoint: form.unapply_endpoint };
  }
  return base;
}

function capTypeBadge(type) {
  const isFlow = type === 'flow';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.2rem 0.55rem',
      borderRadius: '999px',
      fontSize: '0.75rem',
      fontWeight: 700,
      background: isFlow ? 'rgba(124, 58, 237, 0.09)' : 'rgba(23, 157, 166, 0.09)',
      color: isFlow ? '#6d28d9' : '#179da6',
      border: `1px solid ${isFlow ? 'rgba(124,58,237,0.25)' : 'rgba(23, 157, 166,0.25)'}`,
    }}>
      {isFlow ? 'Flow' : 'Financial'}
    </span>
  );
}

// ── Platform admin view ───────────────────────────────────────────────────────

export const PlatformCapabilitiesView = () => {
  const [capabilities, setCapabilities] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create / edit form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Expanded detail panel per capability
  const [expandedId, setExpandedId] = useState(null);

  // Access / modification list actions
  const [accessOrgInput, setAccessOrgInput] = useState({});
  const [modOrgInput, setModOrgInput] = useState({});
  const [attachOrgInput, setAttachOrgInput] = useState({});
  const [, setActionLoading] = useState({});
  const [actionError, setActionError] = useState({});

  // Deletion confirmation
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchCapabilities();
    fetchOrganizations();
  }, []);

  const fetchCapabilities = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/capabilities`, { headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load capabilities');
      setCapabilities(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-settings/organizations`, { headers: authHeaders() });
      if (!res.ok) return;
      setOrganizations(await res.json());
    } catch {}
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        capability_type: form.capability_type,
        config: buildConfig(form),
        is_access_public: form.is_access_public,
        is_modification_public: form.is_modification_public,
      };

      const url = editingId
        ? `${API_BASE_URL}/api/admin/capabilities/${editingId}`
        : `${API_BASE_URL}/api/admin/capabilities`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to save capability');

      setSuccess(editingId ? 'Capability updated.' : 'Capability created.');
      setShowForm(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
      await fetchCapabilities();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (cap) => {
    const cfg = cap.config || {};
    setForm({
      name: cap.name,
      description: cap.description || '',
      capability_type: cap.capability_type,
      is_access_public: cap.is_access_public,
      is_modification_public: cap.is_modification_public,
      price: cfg.price ?? '',
      reoccurring: cfg.reoccurring ?? true,
      apply_endpoint: cfg.apply_endpoint ?? '',
      unapply_endpoint: cfg.unapply_endpoint ?? '',
    });
    setEditingId(cap.id);
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/capabilities/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Delete failed');
      setSuccess('Capability deleted.');
      await fetchCapabilities();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const listAction = async (capId, path, method, onSuccess) => {
    const key = `${capId}-${path}-${method}`;
    setActionLoading(l => ({ ...l, [key]: true }));
    setActionError(a => ({ ...a, [key]: '' }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/capabilities/${capId}/${path}`, {
        method,
        headers: authHeaders(),
        body: method !== 'DELETE' ? JSON.stringify(onSuccess.body) : undefined,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Action failed');
      if (onSuccess.clear) onSuccess.clear();
      await fetchCapabilities();
    } catch (e) {
      setActionError(a => ({ ...a, [key]: e.message }));
    } finally {
      setActionLoading(l => ({ ...l, [key]: false }));
    }
  };

  const renderCapabilityPanel = (cap) => {
    const isExpanded = expandedId === cap.id;
    const attachedOrgIds = cap.organization_attachments.map(a => a.organization_id);
    const accessOrgIds = cap.access_entries.map(e => e.organization_id);
    const modOrgIds = cap.modification_entries.map(e => e.organization_id);

    const hasAccess = (o) => cap.is_access_public || accessOrgIds.includes(o.id);
    const freeOrgsForAccess = organizations.filter(o => !accessOrgIds.includes(o.id));
    const freeOrgsForMod = organizations.filter(o => !modOrgIds.includes(o.id) && hasAccess(o));
    const orgsForAttach = organizations.filter(o => !attachedOrgIds.includes(o.id) && hasAccess(o));

    return (
      <div key={cap.id} style={{
        border: '1px solid #d9d9d9',
        borderRadius: '12px',
        marginBottom: '0.75rem',
        background: '#fff',
        overflow: 'hidden',
      }}>
        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          cursor: 'pointer',
          background: isExpanded ? '#f8f9fc' : '#fff',
        }} onClick={() => setExpandedId(isExpanded ? null : cap.id)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {isExpanded ? <ChevronDown size={16} color="#6f7276" /> : <ChevronRight size={16} color="#6f7276" />}
            <strong style={{ color: '#373737' }}>{cap.name}</strong>
            {capTypeBadge(cap.capability_type)}
            {cap.is_access_public && (
              <span style={{ ...infoBoxStyle, padding: '0.15rem 0.5rem', fontSize: '0.73rem' }}>Public</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
            <button className="admin-btn-switch" onClick={() => startEdit(cap)}>
              <Edit2 size={14} style={{ marginRight: '0.3rem' }} /> Edit
            </button>
            <button
              className="admin-btn-remove"
              onClick={() => handleDelete(cap.id)}
              disabled={deletingId === cap.id}
            >
              <Trash2 size={14} style={{ marginRight: '0.3rem' }} />
              {deletingId === cap.id ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div style={{ padding: '1.25rem', borderTop: '1px solid #d9d9d9' }}>
            {cap.description && (
              <p style={{ color: '#6f7276', marginBottom: '1.25rem', marginTop: 0 }}>{cap.description}</p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={infoBoxStyle}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#373737' }}>Price</strong>
                ${cap.config?.price ?? '-'} / {cap.config?.reoccurring ? 'month' : 'one-time'}
              </div>
              <div style={infoBoxStyle}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#373737' }}>Access</strong>
                {cap.is_access_public ? 'Public (all orgs)' : `${cap.access_entries.length} org(s)`}
              </div>
              <div style={infoBoxStyle}>
                <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#373737' }}>Attached to</strong>
                {cap.organization_attachments.length} org(s)
              </div>
            </div>

            {/* Access list */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', color: '#373737', fontSize: '0.9rem', fontWeight: 700 }}>
                <Lock size={14} style={{ marginRight: '0.4rem', color: '#179da6' }} />
                Access List {cap.is_access_public && <span style={{ color: '#6f7276', fontWeight: 400 }}>(overridden by Public flag)</span>}
              </h4>
              {cap.access_entries.length > 0 ? (
                <div className="admin-table-wrapper" style={{ marginBottom: '0.75rem' }}>
                  <table className="admin-table">
                    <thead><tr><th>Organization</th><th>Actions</th></tr></thead>
                    <tbody>
                      {cap.access_entries.map(e => (
                        <tr key={e.organization_id}>
                          <td>{e.organization_name}</td>
                          <td>
                            <button className="admin-btn-remove" onClick={() =>
                              listAction(cap.id, `access/${e.organization_id}`, 'DELETE', {})
                            }>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#6f7276', fontSize: '0.88rem', marginBottom: '0.75rem' }}>No organizations on access list.</div>
              )}
              {freeOrgsForAccess.length > 0 && (
                <div style={alignedRow}>
                  <select
                    value={accessOrgInput[cap.id] || ''}
                    onChange={e => setAccessOrgInput(v => ({ ...v, [cap.id]: e.target.value }))}
                    style={styledSelect}
                  >
                    <option value="">Add organization</option>
                    {freeOrgsForAccess.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <button className="primary-button" style={{ flexShrink: 0 }}
                    disabled={!accessOrgInput[cap.id]}
                    onClick={() => listAction(cap.id, 'access', 'POST', {
                      body: { organization_id: parseInt(accessOrgInput[cap.id], 10) },
                      clear: () => setAccessOrgInput(v => ({ ...v, [cap.id]: '' })),
                    })}
                  >Add</button>
                </div>
              )}
              {actionError[`${cap.id}-access-POST`] && (
                <div style={{ ...errorBoxStyle, marginTop: '0.5rem' }}>{actionError[`${cap.id}-access-POST`]}</div>
              )}
            </div>

            {/* Modification list */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#373737', fontSize: '0.9rem', fontWeight: 700 }}>
                <Unlock size={14} style={{ marginRight: '0.4rem', color: '#179da6' }} />
                Self-Management List {cap.is_modification_public && <span style={{ color: '#6f7276', fontWeight: 400 }}>(overridden by Public flag)</span>}
              </h4>
              {cap.is_modification_public && !cap.is_access_public && (
                <div style={{ ...warnBoxStyle, marginBottom: '0.75rem', fontSize: '0.82rem' }}>
                  Self-management is public, but access is restricted, only orgs on the access list can see and self-manage this capability.
                </div>
              )}
              {cap.modification_entries.length > 0 ? (
                <div className="admin-table-wrapper" style={{ marginBottom: '0.75rem' }}>
                  <table className="admin-table">
                    <thead><tr><th>Organization</th><th>Actions</th></tr></thead>
                    <tbody>
                      {cap.modification_entries.map(e => (
                        <tr key={e.organization_id}>
                          <td>{e.organization_name}</td>
                          <td>
                            <button className="admin-btn-remove" onClick={() =>
                              listAction(cap.id, `modification/${e.organization_id}`, 'DELETE', {})
                            }>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#6f7276', fontSize: '0.88rem', marginBottom: '0.75rem' }}>No organizations on self-management list.</div>
              )}
              {freeOrgsForMod.length > 0 && (
                <div style={alignedRow}>
                  <select
                    value={modOrgInput[cap.id] || ''}
                    onChange={e => setModOrgInput(v => ({ ...v, [cap.id]: e.target.value }))}
                    style={styledSelect}
                  >
                    <option value="">Add organization</option>
                    {freeOrgsForMod.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <button className="primary-button" style={{ flexShrink: 0 }}
                    disabled={!modOrgInput[cap.id]}
                    onClick={() => listAction(cap.id, 'modification', 'POST', {
                      body: { organization_id: parseInt(modOrgInput[cap.id], 10) },
                      clear: () => setModOrgInput(v => ({ ...v, [cap.id]: '' })),
                    })}
                  >Add</button>
                </div>
              )}
              {!cap.is_modification_public && freeOrgsForMod.length === 0 && !cap.is_access_public && cap.access_entries.length === 0 && (
                <div style={{ color: '#6f7276', fontSize: '0.82rem' }}>Add organizations to the access list first.</div>
              )}
              {actionError[`${cap.id}-modification-POST`] && (
                <div style={{ ...errorBoxStyle, marginTop: '0.5rem' }}>{actionError[`${cap.id}-modification-POST`]}</div>
              )}
            </div>

            {/* Org attachments */}
            <div>
              <h4 style={{ margin: '0 0 0.75rem', color: '#373737', fontSize: '0.9rem', fontWeight: 700 }}>
                <Link size={14} style={{ marginRight: '0.4rem', color: '#179da6' }} />
                Organization Attachments
              </h4>
              {cap.organization_attachments.length > 0 ? (
                <div className="admin-table-wrapper" style={{ marginBottom: '0.75rem' }}>
                  <table className="admin-table">
                    <thead>
                      <tr><th>Organization</th><th>Attached At</th><th>By User</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {cap.organization_attachments.map(a => (
                        <tr key={a.organization_id}>
                          <td>{a.organization_name}</td>
                          <td>{new Date(a.attached_at).toLocaleDateString()}</td>
                          <td>{a.attached_by_user_id ?? '-'}</td>
                          <td>
                            <button className="admin-btn-remove" onClick={() =>
                              listAction(cap.id, `organizations/${a.organization_id}/detach`, 'DELETE', {})
                            }>Detach</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#6f7276', fontSize: '0.88rem', marginBottom: '0.75rem' }}>Not attached to any organization.</div>
              )}
              {orgsForAttach.length > 0 ? (
                <div style={alignedRow}>
                  <select
                    value={attachOrgInput[cap.id] || ''}
                    onChange={e => setAttachOrgInput(v => ({ ...v, [cap.id]: e.target.value }))}
                    style={styledSelect}
                  >
                    <option value="">Attach to organization</option>
                    {orgsForAttach.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <button className="primary-button" style={{ flexShrink: 0 }}
                    disabled={!attachOrgInput[cap.id]}
                    onClick={() => listAction(cap.id, `organizations/${attachOrgInput[cap.id]}/attach`, 'POST', {
                      body: {},
                      clear: () => setAttachOrgInput(v => ({ ...v, [cap.id]: '' })),
                    })}
                  >Attach</button>
                </div>
              ) : !cap.is_access_public && cap.access_entries.length === 0 ? (
                <div style={{ color: '#6f7276', fontSize: '0.82rem' }}>Add organizations to the access list to enable attachment.</div>
              ) : null}
              {actionError[`${cap.id}-organizations/${attachOrgInput[cap.id]}/attach-POST`] && (
                <div style={{ ...errorBoxStyle, marginTop: '0.5rem' }}>
                  {actionError[`${cap.id}-organizations/${attachOrgInput[cap.id]}/attach-POST`]}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {error && <div style={{ ...errorBoxStyle, marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ ...successBoxStyle, marginBottom: '1rem' }}>{success}</div>}

      {/* Create / Edit form */}
      {showForm ? (
        <div className="admin-create-user-panel" style={{ marginBottom: '1.5rem' }}>
          <div className="admin-create-user-panel-header">
            <h3 className="admin-create-user-panel-title">
              {editingId ? 'Edit Capability' : 'New Capability'}
            </h3>
          </div>
          {formError && <div style={{ ...errorBoxStyle, marginBottom: '1rem' }}>{formError}</div>}
          <form onSubmit={handleSave}>
            <div className="grid-container admin-create-user-grid" style={{ marginBottom: '1rem' }}>
              <div className="input-group">
                <label>Name</label>
                <input name="name" value={form.name} onChange={handleFormChange} required placeholder="e.g. Premium Analytics" />
              </div>
              <div className="input-group">
                <label>Type</label>
                <select name="capability_type" value={form.capability_type} onChange={handleFormChange}>
                  <option value="financial">Financial</option>
                  <option value="flow">Flow</option>
                </select>
              </div>
              <div className="input-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Describe what this capability does..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div className="input-group">
                <label>Price (USD/month)</label>
                <input
                  name="price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.price}
                  onChange={handleFormChange}
                  required
                  placeholder="49.00"
                />
              </div>
              <div className="input-group">
                <label>Recurring Billing</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input type="checkbox" id="reoccurring" name="reoccurring" checked={form.reoccurring} onChange={handleFormChange} style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
                  <label htmlFor="reoccurring" style={{ cursor: 'pointer', margin: 0, textTransform: 'none', fontSize: '0.9rem', fontWeight: 'normal', color: '#4a4a4a' }}>Enabled</label>
                </div>
              </div>
              {form.capability_type === 'flow' && (
                <>
                  <div className="input-group">
                    <label>Apply Endpoint</label>
                    <input name="apply_endpoint" value={form.apply_endpoint} onChange={handleFormChange} required placeholder="https://..." />
                  </div>
                  <div className="input-group">
                    <label>Unapply Endpoint</label>
                    <input name="unapply_endpoint" value={form.unapply_endpoint} onChange={handleFormChange} required placeholder="https://..." />
                  </div>
                </>
              )}
              <div className="input-group">
                <label>Public Access</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input type="checkbox" id="is_access_public" name="is_access_public" checked={form.is_access_public} onChange={handleFormChange} style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
                  <label htmlFor="is_access_public" style={{ cursor: 'pointer', margin: 0, textTransform: 'none', fontSize: '0.9rem', fontWeight: 'normal', color: '#4a4a4a' }}>All orgs can see</label>
                </div>
              </div>
              <div className="input-group">
                <label>Public Self-Management</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input type="checkbox" id="is_modification_public" name="is_modification_public" checked={form.is_modification_public} onChange={handleFormChange} style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
                  <label htmlFor="is_modification_public" style={{ cursor: 'pointer', margin: 0, textTransform: 'none', fontSize: '0.9rem', fontWeight: 'normal', color: '#4a4a4a' }}>All orgs can attach/detach</label>
                </div>
              </div>
            </div>
            <div className="admin-button-row">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Capability' : 'Create Capability'}
              </button>
              <button className="admin-btn-cancel" type="button" onClick={() => {
                setShowForm(false); setEditingId(null); setForm({ ...EMPTY_FORM }); setFormError('');
              }} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <button className="primary-button" style={{ marginBottom: '1.5rem' }} onClick={() => {
          setShowForm(true); setEditingId(null); setForm({ ...EMPTY_FORM }); setFormError(''); setSuccess('');
        }}>
          <Plus size={15} style={{ marginRight: '0.4rem' }} /> New Capability
        </button>
      )}

      {/* Capability list */}
      {loading ? (
        <div className="admin-no-users">Loading capabilities...</div>
      ) : capabilities.length === 0 ? (
        <div style={infoBoxStyle}>No capabilities created yet. Click "New Capability" to get started.</div>
      ) : (
        capabilities.map(renderCapabilityPanel)
      )}
    </>
  );
};

// ── Org admin view ────────────────────────────────────────────────────────────

export const OrgCapabilitiesView = ({ searchable = false, gridLayout = false }) => {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionState, setActionState] = useState({});
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchMyCapabilities();
  }, []);

  const fetchMyCapabilities = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/capabilities`, { headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to load capabilities');
      setCapabilities(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (cap) => {
    const key = cap.id;
    setActionState(s => ({ ...s, [key]: { loading: true, error: '' } }));
    try {
      const path = cap.is_attached ? 'detach' : 'attach';
      const method = cap.is_attached ? 'DELETE' : 'POST';
      const res = await fetch(`${API_BASE_URL}/api/capabilities/${cap.id}/${path}`, {
        method,
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Action failed');
      await fetchMyCapabilities();
    } catch (e) {
      setActionState(s => ({ ...s, [key]: { loading: false, error: e.message } }));
    } finally {
      setActionState(s => ({ ...s, [key]: { ...(s[key] || {}), loading: false } }));
    }
  };

  // ── List-style card (default, used on the Capabilities admin page) ──────────
  const renderListCard = (cap) => {
    const state = actionState[cap.id] || {};
    return (
      <div key={cap.id} style={{
        border: '1px solid #d9d9d9',
        borderRadius: '12px',
        padding: '1.25rem 1.5rem',
        background: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
              <strong style={{ color: '#373737', fontSize: '1rem' }}>{cap.name}</strong>
              {cap.is_attached && (
                <span style={{
                  padding: '0.2rem 0.55rem',
                  borderRadius: '999px',
                  fontSize: '0.73rem',
                  fontWeight: 700,
                  background: 'rgba(16,185,129,0.09)',
                  color: '#047857',
                  border: '1px solid rgba(16,185,129,0.25)',
                }}>Active</span>
              )}
            </div>
            {cap.description && (
              <p style={{ margin: '0 0 0.4rem', color: '#6f7276', fontSize: '0.9rem' }}>{cap.description}</p>
            )}
            {cap.price != null && (
              <p style={{ margin: '0 0 0.25rem', color: '#373737', fontSize: '0.88rem', fontWeight: 600 }}>
                ${cap.price.toFixed(2)} / {cap.reoccurring ? 'month' : 'one-time'}
              </p>
            )}
            {cap.is_attached && cap.attached_at && (
              <p style={{ margin: 0, color: '#6f7276', fontSize: '0.82rem' }}>
                Attached since {new Date(cap.attached_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <div style={{ flexShrink: 0 }}>
            {cap.can_self_manage ? (
              <button
                className={cap.is_attached ? 'admin-btn-remove' : 'primary-button'}
                onClick={() => handleToggle(cap)}
                disabled={state.loading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {cap.is_attached
                  ? <><Link2 size={14} /> {state.loading ? 'Detaching...' : 'Detach'}</>
                  : <><Link size={14} /> {state.loading ? 'Attaching...' : 'Attach'}</>
                }
              </button>
            ) : (
              <div style={{ ...infoBoxStyle, fontSize: '0.83rem', color: '#4a5568' }}>
                To {cap.is_attached ? 'manage' : 'attach'} this capability, contact{' '}
                <a href="mailto:sales@nexopta.com" style={{ color: '#179da6' }}>sales@nexopta.com</a>
              </div>
            )}
          </div>
        </div>
        {state.error && <div style={{ ...errorBoxStyle, marginTop: '0.75rem' }}>{state.error}</div>}
      </div>
    );
  };

  // ── Box-style card (grid, mirrors the "Select plans" cards) ─────────────────
  const renderBoxCard = (cap) => {
    const state = actionState[cap.id] || {};
    return (
      <div
        key={cap.id}
        className={`admin-create-user-mode-card ${cap.is_attached ? 'is-selected' : ''}`}
        style={{ cursor: 'default' }}
      >
        <div className="admin-create-user-mode-title-row" style={{ justifyContent: 'space-between' }}>
          <span className="admin-create-user-mode-title">{cap.name}</span>
          {cap.is_attached && (
            <span className="admin-user-count-badge" style={{ marginLeft: 0 }}>Active</span>
          )}
        </div>

        {cap.price != null && (
          <div style={{ color: '#373737', fontWeight: 700, fontSize: '1.15rem' }}>
            ${cap.price.toFixed(2)}
            <span style={{ color: '#6f7276', fontSize: '0.85rem', fontWeight: 500 }}>
              {' '}/ {cap.reoccurring ? 'mo' : 'one-time'}
            </span>
          </div>
        )}

        {cap.description && (
          <p className="admin-create-user-mode-copy" style={{ margin: 0, fontSize: '0.88rem', flexGrow: 1 }}>
            {cap.description}
          </p>
        )}

        {cap.is_attached && cap.attached_at && (
          <p className="admin-create-user-mode-copy" style={{ margin: 0, fontSize: '0.8rem' }}>
            Attached since {new Date(cap.attached_at).toLocaleDateString()}
          </p>
        )}

        <div style={{ marginTop: 'auto', paddingTop: '0.25rem' }}>
          {cap.can_self_manage ? (
            <button
              className={cap.is_attached ? 'admin-btn-remove' : 'primary-button'}
              onClick={() => handleToggle(cap)}
              disabled={state.loading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', width: '100%' }}
            >
              {cap.is_attached
                ? <><Link2 size={14} /> {state.loading ? 'Detaching...' : 'Detach'}</>
                : <><Link size={14} /> {state.loading ? 'Attaching...' : 'Attach'}</>
              }
            </button>
          ) : (
            <div style={{ ...infoBoxStyle, fontSize: '0.8rem', color: '#4a5568' }}>
              To {cap.is_attached ? 'manage' : 'attach'}, contact{' '}
              <a href="mailto:sales@nexopta.com" style={{ color: '#179da6' }}>sales@nexopta.com</a>
            </div>
          )}
          {state.error && <div style={{ ...errorBoxStyle, marginTop: '0.6rem' }}>{state.error}</div>}
        </div>
      </div>
    );
  };

  if (loading) return <div className="admin-no-users">Loading capabilities...</div>;
  if (error) return <div style={errorBoxStyle}>{error}</div>;
  if (capabilities.length === 0) {
    return <div style={infoBoxStyle}>No capabilities are available to your organization.</div>;
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? capabilities.filter(cap =>
        cap.name.toLowerCase().includes(q) ||
        (cap.description || '').toLowerCase().includes(q)
      )
    : capabilities;

  const searchBar = searchable && (
    <div className="admin-user-toolbar">
      <div className="admin-user-toolbar-left">
        <Search size={16} color="#6f7276" style={{ flexShrink: 0 }} />
        <input
          className="admin-user-search"
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search capabilities by name or description..."
        />
        {query && (
          <button className="admin-user-clear-btn" onClick={() => setQuery('')}>
            ✕ Clear
          </button>
        )}
      </div>
      <div className="admin-user-toolbar-right">
        <span className="admin-user-count-label">
          {query
            ? `${filtered.length.toLocaleString()} of ${capabilities.length.toLocaleString()} capabilities`
            : `${capabilities.length.toLocaleString()} capabilities`}
        </span>
      </div>
    </div>
  );

  return (
    <>
      {searchBar}
      {filtered.length === 0 ? (
        <div style={infoBoxStyle}>No capabilities match "{query}".</div>
      ) : gridLayout ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {filtered.map(renderBoxCard)}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filtered.map(renderListCard)}
        </div>
      )}
    </>
  );
};

// ── Page root ─────────────────────────────────────────────────────────────────

const Capabilities = () => {
  const { isPlatformAdmin, isOrganizationAdmin } = useContext(UserContext);

  if (!isPlatformAdmin && !isOrganizationAdmin) {
    return (
      <PageContainer title="Capabilities" subtitle="Access denied">
        <div style={errorBoxStyle}>You do not have permission to access this page.</div>
      </PageContainer>
    );
  }

  if (isPlatformAdmin) {
    return (
      <PageContainer
        title="Capabilities"
        subtitle="Create and manage platform capabilities, control organization access, and attach capabilities to organizations"
      >
        <section className="account-section">
          <h2 className="section-header">
            <Zap className="admin-section-icon" />
            Capability Management
          </h2>
          <PlatformCapabilitiesView />
        </section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Capabilities"
      subtitle="View and manage capabilities available to your organization"
    >
      <section className="account-section">
        <h2 className="section-header">
          <Zap className="admin-section-icon" />
          Your Capabilities
        </h2>
        <OrgCapabilitiesView />
      </section>
    </PageContainer>
  );
};

export default Capabilities;
